const WS_RETRY_ATTEMPTS = 10;
const WS_RETRY_BASE_MS = 1_000;
const WS_RETRY_MAX_MS = 30_000;
const WS_KEEPALIVE_MS = 10_000;

type WsListener = () => void;

type WsConnectionEvents = {
  onClose: (listener: WsListener) => () => void;
  emitClose: () => void;
  /** The socket reconnected after an unexpected close. */
  onReconnect: (listener: WsListener) => () => void;
  emitReconnect: () => void;
};

const closeListeners = new Set<WsListener>();
const reconnectListeners = new Set<WsListener>();

const subscribe =
  (listeners: Set<WsListener>) =>
  (listener: WsListener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

const emit = (listeners: Set<WsListener>, name: string) => {
  for (const listener of listeners) {
    try {
      listener();
    } catch (error) {
      console.error(
        `[wsConnectionEvents] listener threw during ${name}`,
        error,
      );
    }
  }
};

/**
 * Module-level pub/sub for WebSocket connection lifecycle events.
 * Used by useConversationEvents to immediately clear streaming UI
 * when the WS connection drops, rather than waiting for stale-turn timeout.
 */
export const wsConnectionEvents: WsConnectionEvents = {
  onClose: subscribe(closeListeners),
  emitClose: () => emit(closeListeners, "emitClose"),
  onReconnect: subscribe(reconnectListeners),
  emitReconnect: () => emit(reconnectListeners, "emitReconnect"),
};

/**
 * Shared graphql-ws reconnection options for both web and native clients.
 * Provides 10 retries with exponential backoff, keepAlive pings, and
 * lifecycle logging with wsConnectionEvents integration.
 */
export const wsReconnectOptions = {
  retryAttempts: WS_RETRY_ATTEMPTS,
  retryWait: async (retries: number) => {
    const delay = Math.min(WS_RETRY_BASE_MS * 2 ** retries, WS_RETRY_MAX_MS);
    await new Promise<void>(resolve => setTimeout(resolve, delay));
  },
  keepAlive: WS_KEEPALIVE_MS,
  on: {
    // graphql-ws re-subscribes on its own after a retry, but events published
    // while the socket was down are gone, so subscribers must re-read.
    connected: (_socket: unknown, _payload: unknown, wasRetry: boolean) => {
      console.debug("[ws] connected");
      if (wasRetry) {
        wsConnectionEvents.emitReconnect();
      }
    },
    closed: () => {
      console.warn("[ws] closed");
      wsConnectionEvents.emitClose();
    },
    error: (error: unknown) => console.error("[ws] error", error),
  },
} as const;
